import React, { useEffect, useMemo } from "react";
import {
  useCanvasStore,
  useHoveredNavTreeNode,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { TempoElement } from "@/utils/tempoElement";
import { findElementByDomUniquePath } from "../Canvas/utils/utils";
import { useSelector } from "react-redux";
import { useExtractElementStore } from "../Canvas/stores/extractElementStore";

const MIN_PADDING = 20;
interface DropIndicatorProps {
  storyboardId: string;
  iframeRef: React.RefObject<HTMLIFrameElement>;
}

const DropIndicator: React.FC<DropIndicatorProps> = ({
  storyboardId,
  iframeRef,
}) => {
  const dropPosition = useCanvasStore((state) => state.dropPosition);

  const { beforeCodebaseId, afterCodebaseId } = dropPosition || {};
  const temporaryStoryboard = useCanvasStore(
    (state) => state.temporaryStoryboard,
  );

  const elementKeyToNavNodeLookup = useCanvasStore(
    (state) => state.elementKeyToNavNodeLookup,
  );
  const sbIdToCodebaseIdToNavNodeLookup = useCanvasStore(
    (state) => state.sbIdToCodebaseIdToNavNodeLookup,
  );
  const selectedElementBoundingBox = useExtractElementStore(
    (state) => state.selectedElementBoundingBox,
  );
  const { hoveredElementKey } = useHoveredNavTreeNode();

  const draggingComponent = useSelector(
    (state: any) => state.iframe.draggingComponent,
  );

  useEffect(() => {
    if (!draggingComponent || !temporaryStoryboard) {
      useCanvasStore.getState().setDropPosition({});
    }
  }, [draggingComponent, temporaryStoryboard]);

  const hasDropPosition = Boolean(dropPosition);

  const indicatorData = useMemo(() => {
    if (!hasDropPosition || !hoveredElementKey || !iframeRef.current) {
      return null;
    }

    const hoveredElement = TempoElement.fromKey(hoveredElementKey);
    if (!hoveredElement.isKnownElement()) {
      return null;
    }

    if (hoveredElement.storyboardId !== storyboardId) {
      return null;
    }

    const hoveredNavNode = elementKeyToNavNodeLookup[hoveredElementKey];
    if (!hoveredNavNode || !hoveredNavNode.children?.length) {
      return null;
    }

    const iframeDoc =
      iframeRef.current.contentDocument ||
      iframeRef.current.contentWindow?.document;
    if (!iframeDoc) {
      return null;
    }

    if (
      temporaryStoryboard &&
      temporaryStoryboard._elementDragData?.selectedElementKey
    ) {
      // Check if indicators are necessary for this element
      const selectedElement = TempoElement.fromKey(
        temporaryStoryboard._elementDragData?.selectedElementKey,
      );
      if (
        !selectedElement.isEmpty() &&
        (selectedElement.isParentOf(hoveredElement) ||
          selectedElement.isEqual(hoveredElement))
      ) {
        return null;
      }

      if (
        hoveredElement.isDirectParentOf(selectedElement) &&
        temporaryStoryboard?._elementDragIndex !== undefined &&
        dropPosition?.index !== undefined
      ) {
        // Dropping in same position, just reset
        if (dropPosition?.index === temporaryStoryboard._elementDragIndex)
          return null;

        // Also dropping in the same position after accounting for the source element being removed
        if (
          dropPosition?.index > temporaryStoryboard._elementDragIndex &&
          dropPosition?.index === temporaryStoryboard._elementDragIndex + 1
        )
          return null;
      }
    }

    const parentElement = findElementByDomUniquePath(
      iframeDoc,
      hoveredNavNode.domUniquePath ?? hoveredNavNode.domUniquePathForComponent,
    );
    if (!parentElement) {
      return null;
    }

    const parentRect = parentElement.getBoundingClientRect();
    const iframeWindow = iframeRef.current.contentWindow;
    if (!iframeWindow) {
      return null;
    }

    const parentStyle = iframeWindow.getComputedStyle(parentElement);
    const display = parentStyle.display;
    const flexDirection = parentStyle.flexDirection;

    // Determine if line should be horizontal or vertical
    let isHorizontal = true;
    if (display === "flex") {
      isHorizontal =
        flexDirection === "column" || flexDirection === "column-reverse";
    } else if (display === "block") {
      isHorizontal = true;
    } else if (display === "grid") {
      // For grid, default to horizontal
      isHorizontal = true;
    } else if (
      display === "inline" ||
      display === "inline-block" ||
      display === "inline-flex"
    ) {
      // For inline, could be either but default to vertical
      isHorizontal = false;
    }

    // Find the position based on beforeCodebaseId or afterCodebaseId
    let lineX = parentRect.left;
    let lineY = parentRect.top;
    let lineWidth = parentRect.width;
    let lineHeight = parentRect.height;

    // Get all child elements for positioning calculations
    const allChildElements: HTMLElement[] = [];
    if (hoveredNavNode.children) {
      for (const child of hoveredNavNode.children) {
        const childElement = findElementByDomUniquePath(
          iframeDoc,
          child.domUniquePath ?? child.domUniquePathForComponent,
        );
        if (childElement) {
          allChildElements.push(childElement);
        }
      }
    }

    // Calculate the appropriate line size based on priority:
    // A) Size of dropping element (if smaller than parent)
    // B) Size of largest child (if smaller than parent)
    // C) Slightly smaller than parent
    const calculateLineSize = (isHorizontal: boolean) => {
      const parentDimension = isHorizontal
        ? parentRect.width
        : parentRect.height;

      // Priority A: Use dropping element size if available and smaller than parent
      if (selectedElementBoundingBox) {
        const droppingElementSize = isHorizontal
          ? selectedElementBoundingBox.width
          : selectedElementBoundingBox.height;
        if (
          droppingElementSize > 0 &&
          droppingElementSize < parentDimension - MIN_PADDING
        ) {
          return Math.min(
            droppingElementSize + MIN_PADDING,
            parentDimension - MIN_PADDING,
          );
        }
      }

      // Priority B: Use largest child size if smaller than parent
      if (allChildElements.length > 0) {
        let largestChildSize = 0;
        for (const child of allChildElements) {
          const childRect = child.getBoundingClientRect();
          const childSize = isHorizontal ? childRect.width : childRect.height;
          largestChildSize = Math.max(largestChildSize, childSize);
        }
        if (
          largestChildSize > 0 &&
          largestChildSize < parentDimension - MIN_PADDING
        ) {
          return Math.min(
            largestChildSize + MIN_PADDING,
            parentDimension - MIN_PADDING,
          );
        }
      }

      // Priority C: Use parent size minus padding
      return parentDimension - MIN_PADDING;
    };

    if (beforeCodebaseId) {
      // Find the element to insert before
      const beforeNavNodes =
        sbIdToCodebaseIdToNavNodeLookup[storyboardId]?.[beforeCodebaseId];
      if (beforeNavNodes && beforeNavNodes.length > 0) {
        const beforeElement = findElementByDomUniquePath(
          iframeDoc,
          beforeNavNodes[0].domUniquePath ??
            beforeNavNodes[0].domUniquePathForComponent,
        );
        if (beforeElement) {
          const beforeRect = beforeElement.getBoundingClientRect();

          // Find the previous sibling element
          const beforeIndex = allChildElements.indexOf(beforeElement);
          const prevElement =
            beforeIndex > 0 ? allChildElements[beforeIndex - 1] : null;

          if (isHorizontal) {
            // Horizontal line (for vertical layouts)
            if (prevElement) {
              // Position between prevElement and beforeElement
              const prevRect = prevElement.getBoundingClientRect();
              lineY = (prevRect.bottom + beforeRect.top) / 2 - 1; // Center between elements, offset by half line height
            } else {
              // Position at the beginning (between parent top and first element)
              lineY = (parentRect.top + beforeRect.top) / 2 - 1;
            }
            // Use priority-based sizing
            lineWidth = calculateLineSize(true);
            lineX = beforeRect.left + (beforeRect.width - lineWidth) / 2; // Center on the child
            // Keep within parent bounds
            lineX = Math.max(
              parentRect.left + MIN_PADDING / 2,
              Math.min(lineX, parentRect.right - lineWidth - MIN_PADDING / 2),
            );
          } else {
            // Vertical line (for horizontal layouts)
            if (prevElement) {
              // Position between prevElement and beforeElement
              const prevRect = prevElement.getBoundingClientRect();
              lineX = (prevRect.right + beforeRect.left) / 2 - 1; // Center between elements, offset by half line width
            } else {
              // Position at the beginning (between parent left and first element)
              lineX = (parentRect.left + beforeRect.left) / 2 - 1;
            }
            // Use priority-based sizing
            lineHeight = calculateLineSize(false);
            lineY = beforeRect.top + (beforeRect.height - lineHeight) / 2; // Center on the child
            // Keep within parent bounds
            lineY = Math.max(
              parentRect.top + MIN_PADDING / 2,
              Math.min(lineY, parentRect.bottom - lineHeight - MIN_PADDING / 2),
            );
          }
        }
      }
    } else if (afterCodebaseId) {
      // Find the element to insert after
      const afterNavNodes =
        sbIdToCodebaseIdToNavNodeLookup[storyboardId]?.[afterCodebaseId];
      if (afterNavNodes && afterNavNodes.length > 0) {
        const afterElement = findElementByDomUniquePath(
          iframeDoc,
          afterNavNodes[0].domUniquePath ??
            afterNavNodes[0].domUniquePathForComponent,
        );
        if (afterElement) {
          const afterRect = afterElement.getBoundingClientRect();

          // Find the next sibling element
          const afterIndex = allChildElements.indexOf(afterElement);
          const nextElement =
            afterIndex < allChildElements.length - 1
              ? allChildElements[afterIndex + 1]
              : null;

          if (isHorizontal) {
            // Horizontal line (for vertical layouts)
            if (nextElement) {
              // Position between afterElement and nextElement
              const nextRect = nextElement.getBoundingClientRect();
              lineY = (afterRect.bottom + nextRect.top) / 2 - 1; // Center between elements, offset by half line height
            } else {
              // Position at the end (between last element and parent bottom)
              lineY = (afterRect.bottom + parentRect.bottom) / 2 - 1;
            }
            // Use priority-based sizing
            lineWidth = calculateLineSize(true);
            lineX = afterRect.left + (afterRect.width - lineWidth) / 2; // Center on the child
            // Keep within parent bounds
            lineX = Math.max(
              parentRect.left + MIN_PADDING / 2,
              Math.min(lineX, parentRect.right - lineWidth - MIN_PADDING / 2),
            );
          } else {
            // Vertical line (for horizontal layouts)
            if (nextElement) {
              // Position between afterElement and nextElement
              const nextRect = nextElement.getBoundingClientRect();
              lineX = (afterRect.right + nextRect.left) / 2 - 1; // Center between elements, offset by half line width
            } else {
              // Position at the end (between last element and parent right)
              lineX = (afterRect.right + parentRect.right) / 2 - 1;
            }
            // Use priority-based sizing
            lineHeight = calculateLineSize(false);
            lineY = afterRect.top + (afterRect.height - lineHeight) / 2; // Center on the child
            // Keep within parent bounds
            lineY = Math.max(
              parentRect.top + MIN_PADDING / 2,
              Math.min(lineY, parentRect.bottom - lineHeight - MIN_PADDING / 2),
            );
          }
        }
      }
    }

    return {
      x: lineX,
      y: lineY,
      width: lineWidth,
      height: lineHeight,
      isHorizontal,
    };
  }, [
    hasDropPosition,
    hoveredElementKey,
    iframeRef,
    storyboardId,
    elementKeyToNavNodeLookup,
    temporaryStoryboard,
    beforeCodebaseId,
    afterCodebaseId,
    dropPosition?.index,
    selectedElementBoundingBox,
    sbIdToCodebaseIdToNavNodeLookup,
  ]);

  if (!indicatorData) {
    return null;
  }

  return (
    <div
      style={{
        position: "absolute",
        left: indicatorData.x,
        top: indicatorData.y,
        width: indicatorData.isHorizontal ? indicatorData.width : 2,
        height: indicatorData.isHorizontal ? 2 : indicatorData.height,
        backgroundColor: "#3b82f6", // Blue color for the drop indicator
        boxShadow: "0 0 4px rgba(59, 130, 246, 0.5)",
        pointerEvents: "none",
        zIndex: 1000,
      }}
    />
  );
};

export default DropIndicator;
